import React, { memo, useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import cn from 'classnames';

import {
	Button,
	Confirm,
	DynamicIconWrapper,
	Icons,
	Modal,
	SliderCheckbox,
	SpinnerOverlay,
	Tooltip } from '@components/common';
import ProfileTabSection from '@components/common/ProfileTabSection';
import TextAvatar from '@components/common/TextAvatar/TextAvatar';
import AssetForm from '@components/ModalComponents/AssetForm';

import { useMutateEditAssetAccessA11y } from '@hooks/assets/useMutateEditAssetAccessA11y';
import { useMutateEditAssetSiteA11y } from '@hooks/assets/useMutateEditAssetSiteA11y';
import { useMutateEditAssetSpecialImplIssues } from '@hooks/assets/useMutateEditAssetSpecialImplIssues';
import { useMutateEditBackhaulA11y } from '@hooks/assets/useMutateEditBackhaulA11y';
import { useMutateEditPwrSupplyA11y } from '@hooks/assets/useMutateEditPwrSupplyA11y';
import { useMutateToggleAssetStatus } from '@hooks/assets/useMutateToggleAssetStatus';
import { useMutateUpdateAsset } from '@hooks/assets/useMutateUpdateAsset';
import { useQueryAssetById } from '@hooks/assets/useQueryAssetById';
import { useQueryAssetProfileIncomingRequests } from '@hooks/assets/useQueryAssetProfileIncomingRequests';
import { useQueryAssetTypes } from '@hooks/assets/useQueryAssetTypes';
import { useQueryUser } from '@hooks/useQueryUser';

import { auth } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { viewOnMap } from '@slices/map';

import { isEmpty } from '@utils/common';
import { dateFormattingWithSlash, formatDate } from '@utils/date';
import { checkGroupPanelAccess } from '@utils/permissions';

import { TAssetProfile } from '@interfaces/assets/asset';

import { EAssetRentStatuses } from '@enums/asset';
import { EOrganizationType } from '@enums/common';

import { mapGroupAccess } from '@constants/menuGroupAccess';
import { MODAL_WIDTH_580 } from '@constants/modals';
import { routes } from '@constants/routes';
import { assetHistoryTooltip, getAssetProfileTabs, tabsNames, viewOnMapTooltip } from './constants';

import AssetOwnerInfo from './AssetOwnerInfo';
import { getCurrentTab } from './getCurrentTab';

import styles from './AssetProfile.module.scss';

const {
	map,
	adminPanel,
} = routes;

const AssetProfile = ({ isManageAsset = false }: { isManageAsset: boolean }) => {
	const {
		accessPermission,
	} = useAppSelector(auth);

	const {
		assetOwnerOrganizationAdministrator,
		assetOwnerAttributeUpdater,
		assetOwnerApprovalUnit,
		assetOwnerManageRequests,
		assetOwnerSignatory,
		networkOperatorAssetEnquirer,
		assetConsumerAssetEnquirer,
	} = accessPermission;

	const { organisationTypes, organisationId } = useQueryUser();

	const isAssetConsumer = organisationTypes.some(type => type.name === EOrganizationType.assetConsumer);

	const isIncomingRequestsTabShown = assetOwnerApprovalUnit || assetOwnerManageRequests || assetOwnerSignatory;
	const isOutgoingRequestsTabShown = networkOperatorAssetEnquirer || assetConsumerAssetEnquirer;
	const isTelecomSitesTabShown = (networkOperatorAssetEnquirer || assetConsumerAssetEnquirer) && !isAssetConsumer;

	const { id = '' } = useParams();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const [currentTab, setCurrentTab] = useState(tabsNames.summary);

	const [isAssetFormModal, setIsAssetFormModal] = useState(false);
	const [isDirtyFormModal, setIsDirtyFormModal] = useState(false);
	const [isChangeVisibilityDialog, setIsChangeVisibilityDialog] = useState(false);

	const toggleAssetModal = useCallback(() => setIsAssetFormModal(prevState => !prevState), []);

	const closeAssetForm = () => setIsAssetFormModal(false);

	const toggleChangeVisibilityDialog = () => setIsChangeVisibilityDialog(prevState => !prevState);

	const { assetById: profile, isLoadingAssetById: isLoadingProfile } = useQueryAssetById(id, true);
	const { handleUpdateBackhaulAccessibility, isLoadingBackhaulAccessibility } = useMutateEditBackhaulA11y(id);
	const { handleUpdatePowerSupplyAvailability, isLoadingPowerSupplyAvailability } = useMutateEditPwrSupplyA11y(id);
	const {
		handleUpdateAssetSpecialImplementationIssues,
		isLoadingAssetSpecialImplementationIssues,
	} = useMutateEditAssetSpecialImplIssues(id);
	const { handleUpdateAssetAccessAvailability, isLoadingAssetAccessAvailability } = useMutateEditAssetAccessA11y(id);
	const { handleUpdateAssetSiteAvailability, isLoadingAssetSiteAvailability } = useMutateEditAssetSiteA11y(id);
	const { handleChangeAssetStatus, isChangingAssetStatus } = useMutateToggleAssetStatus(
		id,
		toggleChangeVisibilityDialog,
	);
	const { handleUpdateAsset, isLoadingUpdateAsset: isUpdatingAsset } = useMutateUpdateAsset(id, [closeAssetForm]);
	const { assetTypesSubtypes, isLoadingAssetTypes } = useQueryAssetTypes({ isAssetTypesSubtypesWithIconUrl: false });

	const isEditAccess = organisationId === profile?.owner?.id
		&& (assetOwnerOrganizationAdministrator || assetOwnerAttributeUpdater);

	const isLoadingUpdateAsset = (
		isLoadingBackhaulAccessibility ||
		isLoadingPowerSupplyAvailability ||
		isLoadingAssetSpecialImplementationIssues ||
		isLoadingAssetAccessAvailability ||
		isLoadingAssetSiteAvailability ||
		isUpdatingAsset ||
		isChangingAssetStatus ||
		isLoadingAssetTypes
	);

	const {
		countOfRecords,
	} = useQueryAssetProfileIncomingRequests({ assetDbId: profile?.id || '', isIncomingRequestsTabShown });

	const isIncomingRequestsEmpty = countOfRecords === 0;

	const changeViewOnMap = () => {
		handleChangeAssetStatus({
			id,
			showOnMap: !!profile?.showOnMap,
		});
	};

	const handleViewOnMap = () => {
		if (!profile?.showOnMap) return;

		const marker: TAssetProfile = profile;

		void dispatch(viewOnMap({ marker }));
		navigate(map.index, { state: { isRedirectedFromAdminPanel: true } });
	};

	const handleViewAssetHistory = () => {
		const url = `${adminPanel.index}/${adminPanel.assetHistory.replace(':id', id)}`;
		navigate(url, { state: { isRedirectedFromAdminPanel: true } });
	};

	const updatedAt = profile?.updatedAt ? dateFormattingWithSlash(+profile?.updatedAt) : null;
	const createdAt = profile?.createdAt ? formatDate(+profile?.createdAt) : profile?.createdAt;
	const lastVerificationDate = profile?.lastVerificationDate ? formatDate(+profile.lastVerificationDate) : null;
	const siteAvailabilityReviewDate = profile?.siteAvailabilityReviewDate
		? formatDate(+profile.siteAvailabilityReviewDate)
		: null;

	const isRentedAsset = profile?.rentStatus?.name === EAssetRentStatuses.active;
	const isAssetInIncomingRequest = profile?.isIncomingRequests;
	const assetTypeId = assetTypesSubtypes.find(({ value }) => profile?.assetType.id === value)?.type?.icon?.id;

	const tabs = useMemo(() => {
		return getAssetProfileTabs({
			isIncomingRequestsTabShown,
			isIncomingRequestsEmpty,
			isOutgoingRequestsTabShown,
			isTelecomSitesTabShown,
		});
	}, [
		isIncomingRequestsTabShown,
		isIncomingRequestsEmpty,
		isOutgoingRequestsTabShown,
		isTelecomSitesTabShown,
	]);

	const getCurrentTabHandler = useCallback(() => getCurrentTab({
		currentTab,
		profile,
		isEditAccess,
		toggleAssetModal,
		handleUpdateAssetAccessAvailability,
		handleUpdateAssetSiteAvailability,
		handleUpdateAssetSpecialImplementationIssues,
		handleUpdateBackhaulAccessibility,
		handleUpdatePowerSupplyAvailability,
		isManageAsset,
	}), [
		currentTab,
		profile,
		isEditAccess,
		toggleAssetModal,
		handleUpdateAssetAccessAvailability,
		handleUpdateAssetSiteAvailability,
		handleUpdateAssetSpecialImplementationIssues,
		handleUpdateBackhaulAccessibility,
		handleUpdatePowerSupplyAvailability,
		isManageAsset,
	]);

	const isMapModuleAvailable = checkGroupPanelAccess(accessPermission, mapGroupAccess);

	const onBackClickHandler = () => navigate(-1);

	const changeVisibilityQuestion = profile?.showOnMap
		? 'Are you sure you want to hide the asset on map?'
		: 'Are you sure you want to show the asset on map?';
	const changeVisibilityConsequences = profile?.showOnMap
		? 'Only you and Network Operators renting the asset will see it on the map. No one else will be able to rent it. Current reservations will not be blocked.'
		: 'Everyone will see the asset on map and will be able to rent it.';

	return (
		<div className={styles.assetProfileContainer}>
			{isLoadingProfile && <SpinnerOverlay />}
			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyFormModal}
				isOpen={isAssetFormModal}
				isScrollableContent
				onClose={closeAssetForm}
				title='Edit asset'
				width={MODAL_WIDTH_580}
			>
				<AssetForm
					assetData={profile}
					isEditMode={true}
					isLoadingForm={isLoadingUpdateAsset}
					setIsDirtyFormModal={setIsDirtyFormModal}
					submitHandler={handleUpdateAsset}
					toggleAssetModal={toggleAssetModal}
				/>
			</Modal>

			<Button
				icon={Icons.arrowLeft()}
				onClick={onBackClickHandler}
				text='Back'
				withoutBackground
			/>
			<div className={styles.header}>
				<div className={cn(styles.whiteWrapper, styles.nameSection)}>
					<div className={styles.icon}>
						<TextAvatar
							avatarId={assetTypeId}
							fontSize='20px'
							isProfileLogo
							isUnknown={!assetTypeId}
							size='75px'
						/>
					</div>
					<div>
						<div className={styles.name}>{profile?.assetId}</div>
						<div className={styles.type}>{profile?.assetType.assetTypeName}</div>
						{profile?.assetSubType?.assetSubTypeName
							&& <div className={styles.subtype}>{profile?.assetSubType?.assetSubTypeName}</div>
						}
					</div>
					<div className={styles.actionsWrapper}>
						{isMapModuleAvailable && (
							<div className={styles.location}>
								<Tooltip content={viewOnMapTooltip}>
									<div onClick={handleViewOnMap}>
										<DynamicIconWrapper
											icon={Icons.location}
											isDisabled={!profile?.showOnMap}
										/>
									</div>
								</Tooltip>
							</div>
						)}
						{isEditAccess && (
							<div className={styles.location}>
								<Tooltip content={assetHistoryTooltip}>
									<div onClick={handleViewAssetHistory}>
										<DynamicIconWrapper icon={Icons.history} />
									</div>
								</Tooltip>
							</div>)
						}
					</div>
				</div>
				<div className={cn(styles.whiteWrapper, styles.assetOnMapSection)}>
					{isEditAccess && (
						<div className={styles.sliderCheckbox}>
							<SliderCheckbox
								checked={!!profile?.showOnMap}
								disabled={isRentedAsset || isAssetInIncomingRequest}
								id='assetOnMap'
								label='Show Asset on the Map'
								onChange={toggleChangeVisibilityDialog}
							/>
						</div>
					)}
					<Modal
						isOpen={isChangeVisibilityDialog}
						onClose={toggleChangeVisibilityDialog}
					>
						<Confirm
							isLoading={isChangingAssetStatus}
							onClose={toggleChangeVisibilityDialog}
							onSubmit={changeViewOnMap}
							question={changeVisibilityQuestion}
						>
							{changeVisibilityConsequences}
						</Confirm>
					</Modal>

					<div className={styles.date}>
						<span>Last record update:</span>
						&nbsp;{updatedAt}
					</div>
					{!!lastVerificationDate
						&& <div className={styles.date}>
							<span>Last verification date:</span>
							&nbsp;{lastVerificationDate}
						</div>
					}
					{siteAvailabilityReviewDate
						&& <div className={styles.date}>
							<span>Site availability review date:</span>
							&nbsp;{siteAvailabilityReviewDate}
						</div>
					}
					<div className={styles.date}><span>Created:</span>&nbsp;{createdAt}</div>
				</div>
				{!isEmpty(profile) && isOutgoingRequestsTabShown && !isIncomingRequestsTabShown
					&& <AssetOwnerInfo owner={profile.owner} />
				}
			</div>
			<ProfileTabSection
				currentTab={currentTab}
				getCurrentTab={getCurrentTabHandler}
				maxWidth='none'
				setCurrentTab={setCurrentTab}
				tabs={tabs}
			/>
		</div>
	);
};

export default memo(AssetProfile);
